import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Grid, Box, makeStyles, IconButton } from "@material-ui/core";
import { MoreVert as MoreVertIcon, SaveAltRounded } from "@material-ui/icons";
import SearchScreenTable from "./SearchScreenTable";
import { ExportMenu } from "components/Widget/components/menus";
import BaseWidgetActionsPopper from "components/BaseWidgetActionsPopper/BaseWidgetActionsPopper";
import {
  calculatePdfImageCoordinatesInMM,
  calculatePdfImageSizeInMM,
} from "utils/widget-helpers";
import {
  exportComponentAsJPEG,
  exportComponentAsPDF,
  exportComponentAsPNG,
} from "react-component-export-image";
import { snakeCase } from "lodash";

const useStyles = makeStyles((theme) => ({
  box: {
    padding: theme.customStyles.baseWidgetBox.padding,
    height: theme.customStyles.baseWidget.height,
  },
}));

const SearchScreenTableWrapper = ({ state }) => {
  const classes = useStyles();
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [exportData, setExportData] = useState({});
  const componentRef = useRef();

  const handleOpenMenu = (event) => {
    setMenuAnchorEl((prevAnchorEl) =>
      prevAnchorEl ? null : event.currentTarget
    );
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const isMenuOpen = Boolean(menuAnchorEl);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (menuAnchorEl && !menuAnchorEl.contains(event.target)) {
        handleCloseMenu();
      }
    };

    document.addEventListener("click", handleOutsideClick);

    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [menuAnchorEl]);

  useEffect(() => {
    if (componentRef.current) {
      componentRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [state]);

  const exportTable = useCallback(
    (fileFormat, fileName) => {
      if (componentRef.current) {
        if (fileFormat === "pdf") {
          const { h, w } = calculatePdfImageSizeInMM(
            componentRef.current.offsetHeight,
            componentRef.current.offsetWidth
          );
          const { x, y } = calculatePdfImageCoordinatesInMM(
            componentRef.current.offsetHeight,
            componentRef.current.offsetWidth
          );

          exportComponentAsPDF(componentRef, {
            fileName: snakeCase(fileName),
            pdfOptions: {
              orientation: "l",
              unit: "mm",
              w,
              h,
              y,
              x,
            },
          });
        }

        if (fileFormat === "jpg") {
          exportComponentAsJPEG(componentRef, {
            fileName: snakeCase(fileName),
          });
        }
        if (fileFormat === "png") {
          exportComponentAsPNG(componentRef, { fileName: snakeCase(fileName) });
        }
      }
      handleCloseMenu();
    },
    [componentRef]
  );

  return (
    <Box
      className={classes.box}
      width="100%"
      p={2}
      boxShadow={2}
      borderTop={3}
      borderColor="#346C9F"
      height="100%"
      style={{ overflowY: "auto" }}
    >
      <Grid container direction="row" justify="flex-end" alignItems="center">
        <Grid item>
          <IconButton aria-label="Options" onClick={handleOpenMenu}>
            <SaveAltRounded />
          </IconButton>
          <BaseWidgetActionsPopper
            anchorEl={menuAnchorEl}
            isOpen={isMenuOpen}
            onClose={handleCloseMenu}
          >
            <ExportMenu exportTable={exportTable} tableData={exportData} />
          </BaseWidgetActionsPopper>
        </Grid>
      </Grid>
      <Grid container ref={componentRef} style={{ paddingTop: "40px" }}>
        <Grid item xs={12}>
          <SearchScreenTable state={state} setTableExportData={setExportData} />
        </Grid>
      </Grid>
    </Box>
  );
};

SearchScreenTableWrapper.propTypes = {
  state: PropTypes.shape({
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
    error: PropTypes.string,
    data: PropTypes.shape({
      rows: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }),
};

SearchScreenTableWrapper.defaultProps = {
  state: {},
};

export default memo(SearchScreenTableWrapper);
